/**
 * Enhanced initialization utilities with auto-redirect support
 * Provides wrapper functions for existing auth handlers with auto-redirect functionality
 */ function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
import { WebAuthHandler } from '../handlers/web';
import { handleAutoRedirect, shouldPerformAutoRedirect } from './auto-redirect';
/**
 * Enhanced web authentication initialization with auto-redirect support
 * This function wraps the standard WebAuthHandler.initialize() method and adds
 * automatic OAuth2 redirect for unauthenticated users
 * 
 * @param handler WebAuthHandler instance
 * @param setState Auth state setter function
 * @param skipAutoRedirect Skip auto-redirect (for testing or special cases)
 * @returns Promise<InitializationResult>
 */ export function initializeWithAutoRedirect(handler, setState) {
    return _initializeWithAutoRedirect.apply(this, arguments);
}
function _initializeWithAutoRedirect() {
    _initializeWithAutoRedirect = _async_to_generator(function(handler, setState) {
        var skipAutoRedirect, result, error;
        var _arguments = arguments;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    skipAutoRedirect = _arguments.length > 2 && _arguments[2] !== void 0 ? _arguments[2] : false;
                    console.log('[EnhancedInit] Starting enhanced initialization with auto-redirect');
                    return [
                        4,
                        handler.initialize(setState)
                    ];
                case 1:
                    result = _state.sent();
                    console.log('[EnhancedInit] Standard initialization result:', {
                        isAuthenticated: result.isAuthenticated,
                        hasUser: !!result.user,
                        hasToken: !!result.token
                    });
                    if (!(!result.isAuthenticated && !skipAutoRedirect && shouldPerformAutoRedirect())) return [
                        3,
                        6
                    ];
                    console.log('[EnhancedInit] User not authenticated - triggering auto-redirect');
                    _state.label = 2;
                case 2:
                    _state.trys.push([
                        2,
                        4,
                        ,
                        5
                    ]);
                    // Trigger auto-redirect to OAuth2 login
                    return [
                        4,
                        handleAutoRedirect({
                            authServerUrl: handler.authServerUrl,
                            provider: 'kakao'
                        })
                    ];
                case 3:
                    _state.sent();
                    // If we reach here, redirect failed (shouldn't normally happen)
                    console.warn('[EnhancedInit] Auto-redirect completed but still in same page');
                    return [
                        3,
                        5
                    ];
                case 4:
                    error = _state.sent();
                    console.error('[EnhancedInit] Auto-redirect failed:', error);
                    return [
                        3,
                        5
                    ];
                case 5:
                    return [
                        3,
                        7
                    ];
                case 6:
                    if (result.isAuthenticated) {
                        console.log('[EnhancedInit] User already authenticated - no redirect needed');
                    } else if (skipAutoRedirect) {
                        console.log('[EnhancedInit] Auto-redirect skipped by flag');
                    } else {
                        console.log('[EnhancedInit] Auto-redirect skipped by shouldPerformAutoRedirect()');
                    }
                    _state.label = 7;
                case 7:
                    return [
                        2,
                        result
                    ];
            }
        });
    });
    return _initializeWithAutoRedirect.apply(this, arguments);
}
/**
 * Create a WebAuthHandler with enhanced initialization that includes auto-redirect
 * This is a factory function that creates a handler with the enhanced behavior
 * 
 * @param authServerUrl Auth server URL (optional)
 * @returns WebAuthHandler with enhanced initialization
 */ export function createEnhancedWebAuthHandler(authServerUrl) {
    var handler = new WebAuthHandler(authServerUrl);
    // Add enhanced initialization method
    handler.initializeWithAutoRedirect = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(setState) {
            var skipAutoRedirect;
            var _arguments = arguments;
            return _ts_generator(this, function(_state) {
                skipAutoRedirect = _arguments.length > 1 && _arguments[1] !== void 0 ? _arguments[1] : false;
                return [
                    2,
                    initializeWithAutoRedirect(handler, setState, skipAutoRedirect)
                ];
            });
        });
        return function(setState) {
            return _ref.apply(this, arguments);
        };
    }();
    return handler;
}
